import { ErrorText } from "@tokenterminal/ui/ErrorText"
import { Select, Option } from "@tokenterminal/ui/Input/Select"
import { type OpenModal } from "@tokenterminal/ui/Modal/Modal"
import { ErrorBoundary } from "components/ErrorBoundary"
import { useTracking } from "components/Tracking"
import { sendEvent } from "modules/analytics"
import {
  ACCESS_ACTIONS,
  ACCESS_COMPONENTS,
  useAccess,
} from "modules/auth/useAccess"
import { LazyAuthPaywallModal } from "modules/paywall/LazyAuthPaywallModal"
import Router, { useRouter } from "next/router"
import { useCallback, useMemo, Suspense, useState } from "react"
import { type Mart } from "types/ApiTypes"
import { userPresetListItem } from "./PresetSelector.css"

interface PresetSelectorComponentProps {
  presets: Array<Mart["presets"][number] & { type?: "user" }>
  preventSelection?: OpenModal
}

interface PresetSelectorProps extends Pick<Mart, "presets"> {
  id: Mart["mart_id"]
}

const PresetsDropdown = ({
  presets = [],
  preventSelection,
}: PresetSelectorComponentProps) => {
  const {
    query: { preset },
  } = useRouter()

  const { options, defaultName } = useMemo(() => {
    let defaultSelectorName
    const mappedSelectors: Array<{ value: string; label: string }> =
      presets.map(({ id, name, is_default, type }) => {
        if (is_default) {
          defaultSelectorName = name
        }

        if (type === "user") {
          return {
            value: id,
            label: name,
            itemProps: { className: userPresetListItem },
          }
        }
        return {
          value: is_default ? "default" : id,
          label: name,
        }
      })
    return {
      options: mappedSelectors,
      defaultName: defaultSelectorName,
    }
  }, [presets])

  const onSelect = useCallback(
    (value: string | null) => {
      if (preventSelection) {
        preventSelection()
        return
      }

      if (!value) {
        return
      }

      const url = new URL(window.location.href)
      if (value !== "default") {
        url.searchParams.set("preset", value)
      } else {
        url.searchParams.delete("preset")
      }
      Router.replace(url, undefined, { shallow: true })
    },
    [preventSelection]
  )

  return (
    <Select
      id="preset-selector"
      name="preset-selector"
      onChange={onSelect}
      defaultSelected={(preset as string | undefined) ?? "default"}
      label="Select Preset"
      disabled={options.length < 2} // Disable preset selector when only Overview preset is available
    >
      {options.map(({ label, value }) => (
        <Option key={value} value={value}>
          {label}
        </Option>
      ))}
    </Select>
  )
}

const ProPresetsDropdown = ({ presets = [] }: PresetSelectorProps) => {
  return <PresetsDropdown presets={presets} />
}

export function PresetSelector({ presets = [], id }: PresetSelectorProps) {
  const isPresetSelectionAllowed = useAccess(
    ACCESS_COMPONENTS.DATASETS,
    ACCESS_ACTIONS.PRESET_SAVE
  )
  const { contextValue } = useTracking()
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)

  if (!presets.length) {
    return null
  }

  return (
    <ErrorBoundary
      fallback={
        <ErrorText size="xs">Failed to load presets selector</ErrorText>
      }
    >
      {isPresetSelectionAllowed ? (
        <Suspense fallback={<PresetsDropdown presets={presets} />}>
          <ProPresetsDropdown presets={presets} id={id} />
        </Suspense>
      ) : (
        <PresetsDropdown
          presets={presets}
          preventSelection={() => {
            setIsAuthModalOpen(true)

            sendEvent("wall", {
              action: "preset_view",
              wall_type: "auth",
              component: "datatable",
              name: id,
              filters: {},
              page_id: contextValue.pageId,
              page_type: contextValue.pageType!,
            })
          }}
        />
      )}
      <LazyAuthPaywallModal
        onClose={() => setIsAuthModalOpen(false)}
        isOpen={isAuthModalOpen}
        setIsOpen={setIsAuthModalOpen}
      />
    </ErrorBoundary>
  )
}
